<template>
  <v-container>
    <div class="back-nav">
      <v-btn text :to="{ name: 'employees' }" exact>
        <v-icon left>mdi-chevron-left</v-icon>
        My employees
      </v-btn>
    </div>
    <div class="header">
      <h2 class="d-sm-inline-block">Employee: {{ name }}</h2>
      <div class="text-right float-sm-right">
        <v-btn
          text
          class="iar-button"
          @click="openIARDialog"
          v-if="isFeatureEnabled('INDIVIDUAL_AUDIT_REPORT')"
        >
          <v-icon size="20" color="primary" class="iar-icon"
            >$arrowDownToLine</v-icon
          >
          <span class="iar-button-text">Individual Audit Report</span>
        </v-btn>
        <v-btn
          text
          class="terminate-button"
          @click="controls.deleteConfirmationDialogOpen = true"
        >
          <v-icon class="mr-1 terminate-button-icon">$boldx</v-icon>
          <span class="terminate-text">Terminate</span>
        </v-btn>
      </div>
    </div>
    <v-row>
      <v-col cols="12" md="7" lg="8">
        <pt-sheet :highlight="cardHeaderColor">
          <v-card-actions class="pa-sm-0">
            <v-container class="text-right pa-sm-0 mr-sm-4">
              <v-btn
                v-if="!controls.editMode"
                text
                @click="controls.editMode = !controls.editMode"
                :block="$vuetify.breakpoint.xsOnly"
                color="primary"
                class="underline-btn"
              >
                <v-icon left>mdi-pencil</v-icon>
                Edit
              </v-btn>
              <v-btn
                v-if="controls.editMode"
                text
                @click="reset()"
                class="cancel-button"
                :block="$vuetify.breakpoint.xsOnly"
              >
                <span>Cancel</span>
              </v-btn>
              <v-btn
                v-if="controls.editMode"
                color="primary"
                @click="saveEmployee()"
                class="save-changes-button"
                :block="$vuetify.breakpoint.xsOnly"
                :disabled="!formValid"
              >
                Save Changes
              </v-btn>
            </v-container>
          </v-card-actions>
          <v-card-text>
            <v-form v-model="formValid">
              <v-container pa-0>
                <v-row no-gutters>
                  <v-col cols="12" lg="3">
                    <div class="information-container-title">
                      Personal Information
                    </div>
                  </v-col>
                  <v-col cols="12" lg="9">
                    <v-row no-gutters>
                      <v-col cols="12" lg="7">
                        <v-text-field
                          filled
                          dense
                          class="first-name"
                          v-model="vm.firstName"
                          label="First Name*"
                          :disabled="!controls.editMode"
                          :rules="[rules.required('First Name')]"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row no-gutters>
                      <v-col cols="12" lg="7">
                        <v-text-field
                          filled
                          dense
                          v-model="vm.lastName"
                          label="Last Name*"
                          :disabled="!controls.editMode"
                          :rules="[rules.required('Last Name')]"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row no-gutters>
                      <v-col cols="12" sm="6" lg="4">
                        <v-text-field
                          filled
                          dense
                          class="mr-sm-4"
                          v-model="vm.ssn"
                          label="SSN*"
                          :append-icon="
                            !controls.editMode
                              ? ''
                              : controls.showSsn
                              ? 'mdi-eye'
                              : 'mdi-eye-off'
                          "
                          :type="controls.showSsn ? 'text' : 'password'"
                          v-mask="
                            controls.showSsn
                              ? controls.mask
                              : controls.hiddenMask
                          "
                          @click:append="controls.showSsn = !controls.showSsn"
                          :disabled="!controls.editMode"
                          :rules="[
                            rules.required('SSN'),
                            rules.nDigits('SSN', 9)
                          ]"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6" lg="4">
                        <date-field
                          v-model="vm.dob"
                          :maxDate="this.getNow()"
                          label="Date of Birth*"
                          :disabled="!controls.editMode"
                          :rules="[rules.required('Date of Birth')]"
                        >
                        </date-field>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
                <v-divider class="my-8"></v-divider>
                <v-row no-gutters>
                  <v-col cols="12" lg="3">
                    <div class="information-container-title">
                      Medical Licensure
                    </div>
                  </v-col>
                  <v-col cols="12" lg="9">
                    <v-row no-gutters>
                      <v-col cols="12" lg="7">
                        <v-text-field
                          filled
                          dense
                          v-mask="controls.npiMask"
                          v-model="vm.npi"
                          label="National Provider Identifier (NPI)"
                          hint="NPI is a unique identifier for covered health care providers."
                          :disabled="!controls.editMode"
                          :rules="[rules.nDigits('NPI', 10)]"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
                <v-divider class="my-8"></v-divider>
                <v-row no-gutters>
                  <v-col cols="12" lg="3">
                    <div class="information-container-title">
                      Address Information
                    </div>
                  </v-col>
                  <v-col cols="12" lg="9">
                    <v-row no-gutters>
                      <v-col cols="12" lg="7">
                        <v-text-field
                          filled
                          dense
                          v-model="vm.street1"
                          label="Address 1"
                          :disabled="!controls.editMode"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row no-gutters>
                      <v-col cols="12" lg="7">
                        <v-text-field
                          filled
                          dense
                          v-model="vm.street2"
                          label="Suite, APT (optional)"
                          :disabled="!controls.editMode"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row no-gutters>
                      <v-col cols="12" sm="8" lg="4">
                        <v-text-field
                          filled
                          dense
                          class="mr-sm-4"
                          v-model="vm.city"
                          label="City"
                          :disabled="!controls.editMode"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="4" lg="3">
                        <v-autocomplete
                          filled
                          dense
                          clearable
                          label="State"
                          :items="states"
                          v-model="vm.state"
                          item-value="state"
                          item-text="code"
                          class="state-selector"
                          :disabled="!controls.editMode"
                        ></v-autocomplete>
                      </v-col>
                    </v-row>
                    <v-row no-gutters>
                      <v-col cols="12" lg="4">
                        <v-text-field
                          filled
                          dense
                          class="mr-lg-4"
                          v-model="vm.postalCode"
                          label="Zip Code"
                          :disabled="!controls.editMode"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-container>
            </v-form>
          </v-card-text>
        </pt-sheet>
      </v-col>
      <v-col cols="12" md="5" lg="4" order="first" order-md="last">
        <div class="exclusion-panel">
          <div class="status-widget py-3 pl-6" :class="monitoringStatusColor">
            <div class="status-text">
              <p>
                <v-icon class="mr-2">{{ statusIcon }}</v-icon>
                Status: {{ statusText }}
              </p>
            </div>
          </div>

          <div class="exclusion-list" v-if="alertGroups && alertGroups.length">
            <h3>
              Exclusion Lists
            </h3>
            <ul v-for="alertGroup in alertGroups" :key="alertGroup.id">
              <template v-for="alert in alertGroup.alerts">
                <!-- hopefully only one for each group -->
                <li :key="alert.id">
                  <a
                    @click="
                      selectedSource = alertGroup.sourceName;
                      getAndSetAlertDetail(alert.id);
                    "
                  >
                    <span class="dot mr-1 error"></span>
                    {{ alertGroup.sourceName }}</a
                  >
                </li>
              </template>
            </ul>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-dialog
      v-model="alertDetail"
      persistent
      max-width="666"
      v-if="alertDetail"
    >
      <foldable-card
        non-foldable
        hide-border
        @closing="alertDetail = null"
        :title="selectedSource"
      >
        <template v-slot:description>
          <span class="dot mr-1 error"></span>
          Employee has been identified on an exclusion list.
        </template>

        <div class="detail-container">
          <h4>Entity Information</h4>
          <div class="table-wrapper">
            <table>
              <tr
                v-for="(val, label) in alertDetail.monitoredSubjectDetails"
                :key="label"
              >
                <th>{{ label }}</th>
                <td>{{ val }}</td>
              </tr>
            </table>
          </div>
        </div>

        <v-divider class="my-8"></v-divider>

        <div class="detail-container">
          <h4>Verification Information</h4>
          <div class="table-wrapper">
            <table>
              <tr v-for="(val, label) in alertDetail.alertDetails" :key="label">
                <th>{{ label }}</th>
                <td>{{ val }}</td>
              </tr>
            </table>
          </div>
        </div>
      </foldable-card>
    </v-dialog>

    <v-dialog
      v-model="controls.iarDialogOpen"
      width="500"
      @click:outside="closeIARDialog(true)"
    >
      <v-card>
        <v-card-title>
          Download Individual Audit Report
          <v-btn icon absolute top right @click="closeIARDialog(true)">
            <v-icon>mdi-window-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pr-12 pb-2">
          Download a PDF for an auditor to show an employee's monitor statuses
          and alerts for a specific date.
          <v-spacer class="pt-3" />
          <v-divider class="pb-4" />
          <date-field
            label="Select Date *"
            v-model="iarReportDate"
            :maxDate="this.getNow()"
          >
          </date-field>
        </v-card-text>
        <v-card-actions class="pl-sm-6 pt-0 pb-4 justify-start">
          <v-btn
            :disabled="isDownloadButtonDisabled"
            color="primary"
            @click="downloadIar"
          >
            Download Report
          </v-btn>
          <v-btn @click="closeIARDialog(true)">
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="controls.deleteConfirmationDialogOpen" width="672">
      <v-card class="pa-4">
        <v-card-title class="justify-center pt-0">
          <v-icon size="48" color="#FE285B">mdi-alert-rhombus</v-icon>
          <v-btn
            icon
            absolute
            top
            right
            @click="controls.deleteConfirmationDialogOpen = false"
          >
            <v-icon>mdi-window-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          Are you sure you want to remove this employee? They will no longer be
          visible and monitoring will no longer be active for this employee.
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="controls.deleteConfirmationDialogOpen = false">
            Cancel
          </v-btn>

          <v-btn color="primary" @click="actuallyTerminate">
            Terminate Employee
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  employeeByIdAPI,
  employeeAlertsAPI,
  employeeAlertByIdAPI,
  employeeIarAPI
} from "@/services/api";
import { datePickerNow } from "@/services/util";
import { mask } from "vue-the-mask";
import {
  get,
  put,
  _delete,
  getPDF,
  getDownloadFilename
} from "@/services/request";
import hasNotifications from "@/mixins/has-notifications";
import cloneDeep from "lodash/cloneDeep";
import FoldableCard from "@/components/FoldableCard";
import PtSheet from "@/components/PtSheet.vue";
import DateField from "@/components/DateField";
import hasConstant from "@/mixins/has-constant";
import * as rules from "@/services/rules";
import { saveFile } from "@/services/download";

const flattenAddress = obj => {
  if (obj.Addresses && obj.Addresses.length) {
    // employee only has 1 address
    // we are doing it in place which is fine
    obj.Address = obj.Addresses[0].Address;
    delete obj.Addresses;
  }
};

export default {
  props: ["employeeId"],
  name: "EmployeeDetail",
  mixins: [hasConstant, hasNotifications],
  components: {
    PtSheet,
    FoldableCard,
    DateField
  },
  directives: {
    mask
  },
  created() {
    this.loadData();
    this.loadAlerts();
  },
  computed: {
    isCompliant() {
      if (!this.vm || !this.vm.status) return true;
      return this.vm.status.name === "COMPLIANT";
    },
    cardHeaderColor() {
      return this.isCompliant ? "success" : "error";
    },
    monitoringStatusColor() {
      return this.isCompliant ? "success-light" : "alert-light";
    },
    name() {
      if (!this.vm.firstName && !this.vm.lastName) return " ";

      if (!this.vm.firstName) return this.vm.lastName;

      return this.vm.firstName + " " + this.vm.lastName;
    },
    statusIcon() {
      if (this.isCompliant) return "$largecompliantcheck";
      else return "$largenoncompliantx";
    },
    statusText() {
      if (this.vm && this.vm.status) return this.vm.status.desc;
      else return "";
    },
    isDownloadButtonDisabled() {
      return !this.iarReportDate;
    }
  },
  methods: {
    downloadIar() {
      getPDF(employeeIarAPI.getUrl(this.employeeId), {
        date: this.iarReportDate,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      })
        .then(response => {
          saveFile(response.data, getDownloadFilename(response));
        })
        .catch(e => {
          console.error(e);
          this.showMessage({
            text: "Error downloading Individual Audit Report",
            mode: "error"
          });
        });
    },
    actuallyTerminate() {
      this.controls.deleteConfirmationDialogOpen = false;
      _delete(employeeByIdAPI.getUrl(this.employeeId)).then(() => {
        this.$router.push({ name: "employees" });
      });
    },
    save: function(date) {
      this.$refs.menu.save(date);
    },
    saveEmployee() {
      put(employeeByIdAPI.getUrl(this.employeeId), this.vm).then(response => {
        if (response) {
          this.controls.editMode = !this.controls.editMode;
        }
      });
    },
    reset() {
      this.vm = cloneDeep(this.lastVm);
      this.controls.editMode = !this.controls.editMode;
    },
    loadData() {
      get(employeeByIdAPI.getUrl(this.employeeId)).then(response => {
        if (response && response.data) {
          this.vm = response.data;
          this.lastVm = cloneDeep(response.data);
        }
      });
    },
    loadAlerts() {
      get(employeeAlertsAPI.getUrl(this.employeeId)).then(response => {
        if (response && response.data) {
          this.alertGroups = response.data;
        }
      });
    },
    getAndSetAlertDetail(alertId) {
      const employeeId = this.employeeId;

      get(employeeAlertByIdAPI.getUrl({ employeeId, alertId })).then(
        response => {
          if (response && response.data) {
            flattenAddress(response.data.monitoredSubjectDetails);
            this.alertDetail = response.data;
          }
        }
      );
    },
    openIARDialog() {
      this.iarReportDate = datePickerNow();
      this.controls.iarDialogOpen = true;
    },
    closeIARDialog(resetDate) {
      this.controls.iarDialogOpen = false;
      if (resetDate) {
        this.iarReportDate = null;
      }
    },
    getNow: datePickerNow
  },
  data: () => ({
    vm: {}, //placeholder
    alertGroups: [],
    alertDetail: null,
    selectedSource: null,
    formValid: false,
    rules,
    iarReportDate: datePickerNow(),
    controls: {
      deleteConfirmationDialogOpen: false,
      iarDialogOpen: false,
      mask: "###-##-####",
      hiddenMask: "#########",
      npiMask: "##########",
      showSsn: false,
      menu: null,
      editMode: false
    }
  })
};
</script>

<style lang="sass" scoped>
h1
  font-size: 40px
  line-height: 50px
  color: $darker-grey

h2
  font-size: 35px
  line-height: 44px
  color: $darker-grey

h3
  font-size: 24px
  font-weight: normal
  line-height: 31px
  color: $darker-grey

/** EMPLOYEE CARD **/
.information-container-title
  margin-bottom: 16px

/** ACTIONS **/
.v-btn.save-changes-button
  margin-left: 8px
  width: 122px
  color: white
  font-size: 16px
  font-weight: bold
  letter-spacing: 0
  line-height: 20px
  text-align: center

/** Exclusion Panels **/
.iar-button
  background-color: white
  border-style: solid
  border-width: 1px
  border-color: $primary
  margin-right: 8px
.iar-icon
  margin-right: 8px
.iar-button-text
  color: $primary
  font-weight: bold

.terminate-button-icon
  ::v-deep svg
    max-width: 18px
    margin-right: 0

.terminate-button
  background-color: $dark-grey
  color: rgb(10, 68, 159)

.terminate-text
  color: $primary
  font-size: 14px
  font-weight: bold
  letter-spacing: 0
  line-height: 18px
  text-align: center

.alert-light
  background-color: $alert-background

.success-light
  background-color: $success-background

.status-widget
  height: 67px
  .status-text
    font-size: 20px
    font-weight: bold
    line-height: 25px
    color: $darker-grey

.exclusion-list
  padding: 0 19px
  h3
    margin-top: 24px
    margin-bottom: 16px
    font-size: 1.25rem
    font-weight: 600
    color: $subtitle
  ul
    padding: 0
  li
    border-top: 1px solid $list-border-color
    list-style: none
    height: 36px
    display: flex
    align-items: center
    a
      color: $grey-text-color
.detail-container
  display: flex
  h4
    flex: 0 0 177px
    margin-top: .4rem
    color: $grey-text-color3
    font-weight: 600
  table-wrapper
    flex: 1 0 auto
  table
    width: 100%
    border-collapse: collapse
    th
      text-align: left
      width: 177px
      color: $table-heading-color
      font-weight: 600
    tr
      height: 36px
      &:not(:last-child)
        border-bottom: 1px solid $table-border-color
    td
      color: $grey-text-color
      font-size: 1rem
      word-break: break-all
</style>
